import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { 
  Check, 
  Loader2, 
  CreditCard, 
  Shield, 
  Zap,
  Star,
  ArrowRight,
  AlertCircle,
  Sliders,
  FileText,
  Bot,
  MessageSquare,
  Database,
  ArrowLeft
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import api from '@/services/api';
import { PublicHeader } from '@/components/common/PublicHeader';
import { PublicFooter } from '@/components/common/PublicFooter';

export const PricingPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [paddleConfig, setPaddleConfig] = useState(null);
  const [pricingConfig, setPricingConfig] = useState(null);

  useEffect(() => {
    loadPlans();
    loadPricingConfig();
    
    // Scroll to custom builder if hash is #custom
    if (window.location.hash === '#custom') {
      setTimeout(() => {
        document.getElementById('custom-plan-builder')?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }, []);

  const loadPlans = async () => {
    try {
      const response = await api.get('/public/plans');
      const sortedPlans = (response.data || []).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      setPlans(sortedPlans);
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPricingConfig = async () => {
    try {
      const response = await api.get('/pricing-config/public');
      setPricingConfig(response.data);
    } catch (error) {
      console.error('Error loading pricing config:', error);
    }
  };

  const handleSelectPlan = async (plan) => {
    if (!isAuthenticated) {
      toast.info('Please sign in to subscribe');
      navigate('/login?redirect=/pricing');
      return;
    }

    setSelectedPlan(plan);
    setProcessing(true);

    try {
      const price = billingCycle === 'monthly' 
        ? parseFloat(plan.price_monthly || plan.monthly_price || plan.price || 0) 
        : parseFloat(plan.price_yearly || plan.annual_price || plan.price || 0);

      const planIdentifier = plan.plan_id || plan.tier;

      if (price <= 0) {
        // Free plan - activate directly
        const response = await api.post('/subscriptions/create', {
          plan_id: planIdentifier,
          billing_cycle: billingCycle
        });
        toast.success('Plan activated! Welcome to ' + plan.name);
        navigate('/dashboard');
        return;
      }

      // Paid plan - use Stripe checkout
      const checkoutData = {
        plan_id: planIdentifier,
        billing_cycle: billingCycle,
        origin_url: window.location.origin
      };
      
      // Add custom plan config if applicable
      if (plan.is_custom && plan.custom_config) {
        checkoutData.is_custom = true;
        checkoutData.custom_config = plan.custom_config;
        checkoutData.custom_price = price;
      }
      
      const response = await api.post('/stripe/checkout', checkoutData);

      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Could not create checkout session. Please try again.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      const msg = error.response?.data?.detail || 'Unable to start checkout. Please try again.';
      toast.error(msg);
    } finally {
      setProcessing(false);
      setSelectedPlan(null);
    }
  };

  const getPrice = (plan) => {
    if (billingCycle === 'annual' && plan.annual_price) {
      return plan.annual_price;
    }
    return plan.monthly_price;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-slate-900 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col">
      <PublicHeader />
      <div className="flex-1 py-16 px-4">
        <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="bg-slate-100 text-slate-900 mb-4">Pricing</Badge>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Choose a plan that fits your needs, or build your own custom plan.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-10">
          <div className="bg-slate-100 p-1 rounded-lg inline-flex">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                billingCycle === 'annual'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Annual
              <span className="ml-1 text-emerald-600 text-xs">Save 15%</span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {plans.slice(0, 3).map((plan, index) => {
            const isPopular = plan.is_popular || index === 1;
            const price = getPrice(plan);
            
            return (
              <Card 
                key={plan.plan_id || plan.tier}
                className={`relative bg-white border-2 transition-all hover:shadow-lg ${
                  isPopular ? 'border-teal-600 shadow-lg scale-105' : 'border-slate-200'
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-teal-600 text-white px-3 py-1">
                      <Star className="w-3 h-3 mr-1 inline" />Most Popular
                    </Badge>
                  </div>
                )}
                
                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-xl text-slate-900">{plan.name}</CardTitle>
                  <CardDescription className="text-slate-600">
                    {plan.description || 'Perfect for getting started'}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="space-y-6">
                  <div className="text-center">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-4xl font-bold text-slate-900">${price}</span>
                      <span className="text-slate-600">/{billingCycle === 'annual' ? 'mo' : 'month'}</span>
                    </div>
                    {billingCycle === 'annual' && (
                      <p className="text-sm text-emerald-600 mt-1">Billed annually</p>
                    )}
                  </div>
                  
                  <ul className="space-y-3">
                    <li className="flex items-center gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      <span>{plan.chatbot_limit || 1} AI Employee{(plan.chatbot_limit || 1) > 1 ? 's' : ''}</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      <span>{plan.document_limit || 50} Documents</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      <span>{(plan.message_limit || 1000).toLocaleString()} Messages/month</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      <span>{plan.document_size_mb || 100}MB Storage</span>
                    </li>
                  </ul>
                  
                  <Button
                    onClick={() => handleSelectPlan(plan)}
                    disabled={processing && (selectedPlan?.plan_id || selectedPlan?.tier) === (plan.plan_id || plan.tier)}
                    className={`w-full ${isPopular ? 'bg-teal-600 hover:bg-teal-700' : 'bg-slate-900 hover:bg-slate-800'} text-white`}
                    data-testid={`select-plan-${plan.plan_id || plan.tier}`}
                  >
                    {processing && (selectedPlan?.plan_id || selectedPlan?.tier) === (plan.plan_id || plan.tier) ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                    ) : (
                      <>Get Started<ArrowRight className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Custom Plan Builder - Always Visible */}
        <div className="mb-8" id="custom-plan-builder">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Need a Custom Plan?</h2>
            <p className="text-slate-600">Design your perfect plan with our interactive builder</p>
          </div>
          <CustomPlanBuilder 
            pricingConfig={pricingConfig}
            billingCycle={billingCycle}
            onSubscribe={handleSelectPlan}
            isAuthenticated={isAuthenticated}
          />
        </div>

        {/* Enterprise CTA - Only for truly unlimited/enterprise needs */}
        <Card className="bg-gradient-to-r from-slate-900 to-slate-800 border-0 text-white mt-8 md:mt-12" data-testid="pricing-enterprise-cta">
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-6">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg md:text-xl font-bold mb-2 text-white">Need Unlimited Everything?</h3>
                <p className="text-sm md:text-base text-slate-200 leading-relaxed">
                  White-label solutions, dedicated infrastructure, custom SLA, and premium support.
                </p>
              </div>
              <Button
                className="bg-white text-slate-900 hover:bg-slate-100 whitespace-nowrap font-semibold shadow-md w-full md:w-auto"
                onClick={() => window.location.href = 'mailto:enterprise@harmonix.info?subject=Enterprise%20Plan%20Inquiry'}
                data-testid="pricing-enterprise-cta-btn"
              >
                Get Enterprise Quote
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Trust Badges */}
        <div className="mt-12 text-center">
          <div className="flex flex-wrap justify-center gap-8 text-slate-600">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-600" />
              <span className="text-sm">SSL Encrypted</span>
            </div>
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-slate-900" />
              <span className="text-sm">Secure Payments</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-600" />
              <span className="text-sm">Cancel Anytime</span>
            </div>
          </div>
        </div>

        {/* Admin Notice */}
        {!paddleConfig?.configured && isAuthenticated && user?.role === 'system_admin' && (
          <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-amber-800 font-medium">Paddle Not Configured</p>
                <p className="text-sm text-amber-700 mt-1">
                  Payment processing is in demo mode. Configure Paddle in Admin Panel → Payments.
                </p>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
      <PublicFooter />
    </div>
  );
};

// Custom Plan Builder Component
const CustomPlanBuilder = ({ pricingConfig, billingCycle, onSubscribe, isAuthenticated }) => {
  const navigate = useNavigate();
  const limits = pricingConfig?.limits || {};
  const unitPrices = pricingConfig?.unit_prices || {};
  
  const [config, setConfig] = useState({
    documents: limits.documents?.min || 100,
    chatbots: limits.chatbots?.min || 3,
    messages: limits.messages?.min || 5000,
    storage_mb: limits.storage_mb?.min || 500
  });
  
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [calculating, setCalculating] = useState(false);
  
  useEffect(() => {
    calculatePrice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);
  
  const calculatePrice = async () => {
    setCalculating(true);
    try {
      const response = await api.post('/pricing-config/calculate', config);
      setCalculatedPrice(response.data);
    } catch (error) {
      console.error('Error calculating price:', error);
    } finally {
      setCalculating(false);
    }
  };
  
  const handleSubscribe = () => {
    if (!isAuthenticated) {
      toast.info('Please sign in to subscribe');
      navigate('/login?redirect=/pricing');
      return;
    }
    
    // Create a custom plan object with the config
    const customPlan = {
      plan_id: 'custom_' + Date.now(),
      name: 'Custom Plan',
      monthly_price: calculatedPrice?.monthly_price,
      annual_price: calculatedPrice?.annual_price,
      document_limit: config.documents,
      chatbot_limit: config.chatbots,
      message_limit: config.messages,
      document_size_mb: config.storage_mb,
      is_custom: true,
      custom_config: {
        documents: config.documents,
        chatbots: config.chatbots,
        messages: config.messages,
        storage_mb: config.storage_mb
      }
    };
    
    onSubscribe(customPlan);
  };
  
  const price = billingCycle === 'annual' 
    ? calculatedPrice?.annual_price 
    : calculatedPrice?.monthly_price;
  
  return (
    <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 mb-8">
      <CardHeader>
        <CardTitle className="text-slate-900 flex items-center gap-2">
          <Sliders className="w-5 h-5 text-slate-900" />
          Build Your Custom Plan
        </CardTitle>
        <CardDescription className="text-slate-600">
          Adjust the sliders to match your exact needs. Pay only for what you use.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Sliders */}
          <div className="space-y-6">
            {/* Documents */}
            <div>
              <div className="flex justify-between mb-2">
                <Label className="text-slate-700 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-900" />
                  Documents
                </Label>
                <span className="text-sm font-medium text-slate-900">{config.documents}</span>
              </div>
              <input
                type="range"
                min={limits.documents?.min || 10}
                max={limits.documents?.max || 1000}
                step={10}
                value={config.documents}
                onChange={(e) => setConfig({ ...config, documents: parseInt(e.target.value) })}
                className="w-full h-2 bg-white rounded-lg appearance-none cursor-pointer accent-teal-600"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>{limits.documents?.min || 10}</span>
                <span>{limits.documents?.max || 1000}</span>
              </div>
            </div>
            
            {/* AI Employees */}
            <div>
              <div className="flex justify-between mb-2">
                <Label className="text-slate-700 flex items-center gap-2">
                  <Bot className="w-4 h-4 text-slate-900" />
                  AI Employees
                </Label>
                <span className="text-sm font-medium text-slate-900">{config.chatbots}</span>
              </div>
              <input
                type="range"
                min={limits.chatbots?.min || 1}
                max={limits.chatbots?.max || 50}
                step={1}
                value={config.chatbots}
                onChange={(e) => setConfig({ ...config, chatbots: parseInt(e.target.value) })}
                className="w-full h-2 bg-white rounded-lg appearance-none cursor-pointer accent-teal-600"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>{limits.chatbots?.min || 1}</span>
                <span>{limits.chatbots?.max || 50}</span>
              </div>
            </div>
            
            {/* Messages */}
            <div>
              <div className="flex justify-between mb-2">
                <Label className="text-slate-700 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-slate-900" />
                  Monthly Messages
                </Label>
                <span className="text-sm font-medium text-slate-900">{config.messages.toLocaleString()}</span>
              </div>
              <input
                type="range"
                min={limits.messages?.min || 1000}
                max={limits.messages?.max || 100000}
                step={1000}
                value={config.messages}
                onChange={(e) => setConfig({ ...config, messages: parseInt(e.target.value) })}
                className="w-full h-2 bg-white rounded-lg appearance-none cursor-pointer accent-teal-600"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>{((limits.messages?.min || 1000) / 1000).toFixed(0)}K</span>
                <span>{((limits.messages?.max || 100000) / 1000).toFixed(0)}K</span>
              </div>
            </div>
            
            {/* Storage */}
            <div>
              <div className="flex justify-between mb-2">
                <Label className="text-slate-700 flex items-center gap-2">
                  <Database className="w-4 h-4 text-slate-900" />
                  Storage
                </Label>
                <span className="text-sm font-medium text-slate-900">
                  {config.storage_mb >= 1000 ? `${(config.storage_mb / 1000).toFixed(1)}GB` : `${config.storage_mb}MB`}
                </span>
              </div>
              <input
                type="range"
                min={limits.storage_mb?.min || 100}
                max={limits.storage_mb?.max || 5000}
                step={100}
                value={config.storage_mb}
                onChange={(e) => setConfig({ ...config, storage_mb: parseInt(e.target.value) })}
                className="w-full h-2 bg-white rounded-lg appearance-none cursor-pointer accent-teal-600"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>{limits.storage_mb?.min || 100}MB</span>
                <span>{((limits.storage_mb?.max || 5000) / 1000).toFixed(0)}GB</span>
              </div>
            </div>
          </div>
          
          {/* Price Summary */}
          <div>
            <div className="bg-white rounded-xl border-2 border-blue-200 p-6 shadow-sm">
              <h4 className="font-semibold text-slate-900 mb-4">Your Custom Plan</h4>
              
              {/* Features */}
              <div className="space-y-2 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{config.documents} Documents</span>
                  <span className="text-slate-700">${calculatedPrice?.breakdown?.documents?.price || '...'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{config.chatbots} AI Employees</span>
                  <span className="text-slate-700">${calculatedPrice?.breakdown?.chatbots?.price || '...'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{config.messages.toLocaleString()} Messages</span>
                  <span className="text-slate-700">${calculatedPrice?.breakdown?.messages?.price || '...'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{config.storage_mb}MB Storage</span>
                  <span className="text-slate-700">${calculatedPrice?.breakdown?.storage_mb?.price || '...'}</span>
                </div>
              </div>
              
              {/* Price */}
              <div className="pt-4 border-t border-slate-200">
                <div className="flex items-baseline justify-between">
                  <span className="text-slate-600">Total</span>
                  <div className="text-right">
                    {calculating ? (
                      <Loader2 className="w-5 h-5 text-slate-900 animate-spin" />
                    ) : (
                      <>
                        <span className="text-3xl font-bold text-slate-900">${price || 0}</span>
                        <span className="text-slate-600">/{billingCycle === 'annual' ? 'mo' : 'month'}</span>
                      </>
                    )}
                  </div>
                </div>
                {billingCycle === 'annual' && (
                  <p className="text-sm text-emerald-600 text-right mt-1">
                    Save 15% with annual billing
                  </p>
                )}
              </div>
              
              {/* Subscribe Button */}
              <Button
                onClick={handleSubscribe}
                className="w-full mt-6 bg-teal-600 hover:bg-teal-700 text-white"
                disabled={calculating || !price}
                data-testid="subscribe-custom-plan"
              >
                Subscribe to Custom Plan
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            
            {/* Info */}
            <p className="text-xs text-slate-500 text-center mt-4">
              Price updates automatically. No hidden fees.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PricingPage;
